/**
 * Server state for the Settings → Watching tab and the homescreen callout (#68-#76, #94).
 *
 * Three readers over two keys. `QK.watch` holds the whole tab (#68: settings blob, stats, paused
 * reasons, bot flag) and every #69 save answers with it, so the cache is **replaced** rather than
 * refetched — the Telegram and Notion tabs work the same way. `QK.watchTelegram` is the watch bot's
 * own #73 state, polled while a handshake code is outstanding, exactly like #52's.
 *
 * The cost estimate is deliberately a **mutation**, not a query: #72 scores an unsaved draft, so it
 * is keyed by nothing and has no cache to go stale — the card fires it debounced and renders the
 * last answer it got.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  TelegramStateResponse,
  WatchEstimateBody,
  WatchEstimateResponse,
  WatchSettingsUpdate,
  WatchSnoozePreset,
  WatchStateResponse,
} from '@lw/shared';
import {
  connectWatchTelegram,
  disconnectWatchTelegram,
  getWatch,
  getWatchTelegram,
  postWatchEstimate,
  postWatchSnooze,
  putWatchSettings,
  testWatchTelegram,
} from '../../api/watch';
import { QK } from '../../api/queryKeys';

/* --------------------------------------------------------------- #68 / #69 */

/**
 * `refetchIntervalMs` exists for the homescreen callout: "132 checks today" moves with every tick
 * and no SSE event says so (`watch.detection` only fires on a catch). The settings tab passes
 * nothing and keeps its plain fetch-on-mount behaviour.
 */
export function useWatchQuery(
  enabled = true,
  options: { refetchIntervalMs?: number } = {},
): UseQueryResult<WatchStateResponse, Error> {
  return useQuery({
    queryKey: QK.watch,
    queryFn: ({ signal }) => getWatch(signal),
    enabled,
    ...(options.refetchIntervalMs === undefined
      ? {}
      : { refetchInterval: options.refetchIntervalMs }),
  });
}

/**
 * #69. A partial blob — one section of the tab at a time — so a Save here never rewrites a field
 * the user did not touch. Enabling or retiming watching changes what every row's hover says, so
 * the two homescreen lists are re-read after it lands.
 */
export function useUpdateWatchSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: WatchSettingsUpdate) => putWatchSettings(body),
    onSuccess: (state) => {
      queryClient.setQueryData(QK.watch, state);
      void queryClient.invalidateQueries({ queryKey: QK.profiles });
      void queryClient.invalidateQueries({ queryKey: QK.groups });
    },
  });
}

/* --------------------------------------------------------------------- #94 */

/**
 * The global snooze — `null` resumes. Answers with the whole #68 payload, so the cache is
 * **replaced**, never prefix-invalidated: `QK.watchTelegram` is a child of `QK.watch`, and a prefix
 * invalidation would throw away the bot's own state. Every row's hover changes with a snooze, so
 * the two homescreen lists are re-read after it lands.
 */
export function useWatchSnooze() {
  const queryClient = useQueryClient();
  return useMutation<WatchStateResponse, Error, WatchSnoozePreset | null>({
    mutationFn: (preset) => postWatchSnooze({ preset }),
    onSuccess: (state) => {
      queryClient.setQueryData(QK.watch, state);
      void queryClient.invalidateQueries({ queryKey: QK.profiles });
      void queryClient.invalidateQueries({ queryKey: QK.groups });
    },
  });
}

/* --------------------------------------------------------------------- #72 */

export function useWatchEstimate() {
  return useMutation<WatchEstimateResponse, Error, WatchEstimateBody>({
    mutationFn: (body) => postWatchEstimate(body),
  });
}

/* --------------------------------------------------------------- #73 - #76 */

/** Poll cadence while a code is outstanding — the same 2 s the viral bot's handshake uses. */
export const WATCH_TELEGRAM_PENDING_POLL_MS = 2000;

export function useWatchTelegramQuery(
  enabled = true,
): UseQueryResult<TelegramStateResponse, Error> {
  return useQuery({
    queryKey: QK.watchTelegram,
    queryFn: ({ signal }) => getWatchTelegram(signal),
    enabled,
    refetchInterval: (query) =>
      query.state.data?.pending == null ? false : WATCH_TELEGRAM_PENDING_POLL_MS,
  });
}

/** Binding changes also flip #68's `telegramConnected`, so the tab state is re-read alongside. */
function useWatchTelegramMutation<TVars>(
  mutationFn: (vars: TVars) => Promise<TelegramStateResponse>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (state) => {
      queryClient.setQueryData(QK.watchTelegram, state);
      // `exact` matters: `QK.watchTelegram` is a child of `QK.watch`, and a prefix invalidation
      // would throw away the payload just written into it.
      void queryClient.invalidateQueries({ queryKey: QK.watch, exact: true });
    },
  });
}

export function useConnectWatchTelegram() {
  return useWatchTelegramMutation((token: string) => connectWatchTelegram(token));
}

export function useTestWatchTelegram() {
  return useWatchTelegramMutation((_vars: void) => testWatchTelegram());
}

/** #75 answers `204`, so the cache is refetched rather than replaced. */
export function useDisconnectWatchTelegram() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => disconnectWatchTelegram(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QK.watchTelegram });
      void queryClient.invalidateQueries({ queryKey: QK.watch, exact: true });
    },
  });
}
